import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';
import SetPricePanel from '../components/SetPricePanel';

const API_BASE = process.env.REACT_APP_API_URL || '';

const GST_STATUSES = ['SUBMITTED','DOCUMENTS_VERIFIED','APPLICATION_FILED','PENDING_CLARIFICATION','GSTIN_ASSIGNED','REJECTED'];

const STATUS_LABEL = {
  SUBMITTED:             { label: 'Submitted',            icon: '📨', cls: 'badge-submitted' },
  DOCUMENTS_VERIFIED:    { label: 'Docs Verified',        icon: '✅', cls: 'badge-approved' },
  APPLICATION_FILED:     { label: 'Filed on Portal',      icon: '🏛', cls: 'badge-filed' },
  PENDING_CLARIFICATION: { label: 'Pending Clarification',icon: '⚠️', cls: 'badge-under_review' },
  GSTIN_ASSIGNED:        { label: 'GSTIN Assigned',       icon: '🎉', cls: 'badge-acknowledged' },
  REJECTED:              { label: 'Rejected',             icon: '❌', cls: 'badge-rejected' },
};

function GstBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, icon: '•', cls: '' };
  return <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>;
}

// ── Admin GST List ──────────────────────────────────────────────
export function AdminGstList() {
  const [apps, setApps]           = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [statusFilter, setFilter] = useState('ALL');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    api.get('/api/gst/all').then(r => { setApps(r.data); setFiltered(r.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = apps;
    if (statusFilter !== 'ALL') list = list.filter(a => a.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a => a.businessName?.toLowerCase().includes(q) || a.pan?.toLowerCase().includes(q) || a.ownerName?.toLowerCase().includes(q));
    }
    setFiltered(list);
  }, [statusFilter, search, apps]);

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="GST Cases" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>GST Registration Cases</h1>
        <p>{apps.length} total applications</p>
      </div>
      <div className="page-wide">
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search by business name, PAN, owner..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff', outline: 'none' }} />
          <select value={statusFilter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff' }}>
            <option value="ALL">All Statuses</option>
            {GST_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{filtered.length} results</span>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr><th>#</th><th>Business</th><th>PAN</th><th>Type</th><th>Owner</th><th>Phone</th><th>GSTIN</th><th>Status</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No applications found</td></tr>}
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td style={{ color: 'var(--muted)' }}>#{a.id}</td>
                    <td><strong style={{ fontSize: '.88rem' }}>{a.businessName}</strong><br /><span style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{a.email}</span></td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.82rem' }}>{a.pan}</td>
                    <td style={{ fontSize: '.82rem' }}>{a.businessType}</td>
                    <td style={{ fontSize: '.85rem' }}>{a.ownerName}</td>
                    <td style={{ fontSize: '.82rem' }}>{a.phone}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--green)', fontWeight: 600 }}>{a.gstin || '—'}</td>
                    <td><GstBadge status={a.status} /></td>
                    <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>{new Date(a.submittedAt).toLocaleDateString('en-IN')}</td>
                    <td><Link to={`/admin/gst/${a.id}`}><button className="btn btn-primary btn-sm">Review</button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Admin GST Detail ────────────────────────────────────────────
export function AdminGstDetail() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [app, setApp]         = useState(null);
  const [status, setStatus]   = useState('');
  const [note, setNote]       = useState('');
  const [gstin, setGstin]     = useState('');
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    Promise.all([
      api.get(`/api/gst/${id}`),
      api.get(`/api/payment/for/GST_REG/${id}`).catch(() => ({ data: null })),
    ]).then(([a, p]) => {
      setApp(a.data); setStatus(a.data.status); setNote(a.data.adminNote || ''); setGstin(a.data.gstin || '');
      setPayment(p.data || null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ status });
      if (note)  params.append('note', note);
      if (gstin) params.append('gstin', gstin);
      const { data } = await api.patch(`/api/gst/${id}/status?${params}`);
      setApp(data);
      showMsg('success', `Status updated to ${status.replace(/_/g, ' ')}`);
    } catch (err) { showMsg('error', err.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  const uploadCertificate = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post(`/api/gst/${id}/upload-certificate`, fd);
    setApp(data); setStatus(data.status);
    showMsg('success', 'GST certificate uploaded — GSTIN assigned!');
  };

  const downloadFile = async (filename, label) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/files/download/${filename}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = label + '_' + (filename.includes('_') ? filename.substring(filename.indexOf('_') + 1) : filename);
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { alert('Download failed'); }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;
  if (!app)    return <div className="centered"><p>Application not found.</p></div>;

  return (
    <>
      <PageTitle title={`GST Case #${app.id}`} />
      <div className="page-hero">
        <div className="page-hero-label">Admin — GST Review</div>
        <h1>GST Application #{app.id}</h1>
        <p>{app.businessName} · {app.pan}</p>
      </div>
      <div className="page-wide">
        <button className="btn btn-outline btn-sm" style={{ marginBottom: '1.5rem' }} onClick={() => navigate('/admin/gst')}>← Back</button>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.5rem' }}>{msg.text}</div>}

        <div className="detail-grid">

          {/* Left */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            <div className="card">
              <SectionTitle>Business Information</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Business Name"     value={app.businessName} />
                <InfoRow label="Business Type"     value={app.businessType} />
                <InfoRow label="Business Category" value={app.businessCategory} />
                <InfoRow label="Annual Turnover"   value={app.turnover} />
                <InfoRow label="PAN"               value={app.pan} />
                <InfoRow label="State"             value={app.state} />
                <InfoRow label="Pincode"           value={app.pincode} />
                <InfoRow label="Address"           value={app.businessAddress} />
              </div>
            </div>

            <div className="card">
              <SectionTitle>Owner Details</SectionTitle>
              <div className="two-col" style={{ gap: '.5rem' }}>
                <InfoRow label="Owner Name" value={app.ownerName} />
                <InfoRow label="Aadhar"     value={app.aadhar} />
                <InfoRow label="Email"      value={app.email} />
                <InfoRow label="Phone"      value={app.phone} />
                <InfoRow label="Bank"       value={app.bankName} />
                <InfoRow label="Account No" value={app.accountNo} />
                <InfoRow label="IFSC"       value={app.ifsc} />
              </div>
            </div>

            <div className="card">
              <SectionTitle>Documents (Download)</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
                {[['PAN Card', app.docPan], ['Aadhar Card', app.docAadhar], ['Address Proof', app.docAddressProof],
                  ['Bank Statement', app.docBankStatement], ['Business Proof', app.docBusinessProof], ['Other', app.docOther]
                ].map(([label, filename]) => filename ? (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 8 }}>
                    <span style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--green)' }}>📄 {label}</span>
                    <button onClick={() => downloadFile(filename, label)}
                      style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                      ⬇ Download
                    </button>
                  </div>
                ) : (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.6rem .9rem', background: 'var(--creamd)', borderRadius: 8, opacity: .5 }}>
                    <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>— {label}</span>
                    <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>Not uploaded</span>
                  </div>
                ))}
              </div>
              {app.remarks && <div className="alert alert-info" style={{ marginTop: '1rem', fontSize: '.82rem' }}>💬 {app.remarks}</div>}
            </div>

            <div className="card">
              <SectionTitle>Upload GST Certificate</SectionTitle>
              {app.docGstCertificate ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.75rem 1rem', background: 'var(--greenl)', border: '1px solid #a8d5b9', borderRadius: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--green)' }}>✅ GST Certificate uploaded</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--muted)' }}>User can download from their portal</div>
                  </div>
                  <button onClick={() => downloadFile(app.docGstCertificate, 'GST_Certificate')}
                    style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 7, padding: '.32rem .8rem', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                    ⬇ Download
                  </button>
                </div>
              ) : (
                <input type="file" accept=".pdf,.jpg,.png" style={{ fontSize: '.82rem' }}
                  onChange={e => { if (e.target.files[0]) uploadCertificate(e.target.files[0]); }} />
              )}
            </div>
          </div>

          {/* Right */}
          <div>
            <div className="card" style={{ position: 'sticky', top: '5rem' }}>
              <SectionTitle>Update Status</SectionTitle>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--muted)', marginBottom: '.35rem' }}>Current</div>
                <GstBadge status={app.status} />
                {app.gstin && <div style={{ marginTop: '.5rem', background: 'var(--greenl)', color: 'var(--green)', padding: '.4rem .75rem', borderRadius: 8, fontSize: '.85rem', fontWeight: 700 }}>GSTIN: {app.gstin}</div>}
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>Set Status</label>
                <select value={status} onChange={e => setStatus(e.target.value)}>
                  {GST_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              <div className="field" style={{ marginBottom: '1rem' }}>
                <label>GSTIN (when assigned)</label>
                <input value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" maxLength={15}
                  style={{ fontFamily: 'monospace', letterSpacing: '.05em' }} />
              </div>

              <div className="field" style={{ marginBottom: '1.25rem' }}>
                <label>Note to User</label>
                <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Shown on user's status tracker..." />
              </div>

              <button className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}
                onClick={saveStatus} disabled={saving}>
                {saving ? <><span className="spinner" /> Saving...</> : '💾 Update Status'}
              </button>

              {/* ── Payment panel ── */}
              <SetPricePanel
                serviceType="GST_REG"
                serviceId={app.id}
                serviceDesc={`GST Registration #${app.id} — ${app.businessName}`}
                userId={app.userId}
                payment={payment}
                onUpdated={p => setPayment(p)}
              />

              {/* Workflow */}
              <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid var(--creamd)' }}>
                <SectionTitle>GST Workflow</SectionTitle>
                {[['1','SUBMITTED','⬇ Download & verify documents'],['2','DOCUMENTS_VERIFIED','✅ Docs OK'],['3','APPLICATION_FILED','🏛 Filed on GST portal'],['4','GSTIN_ASSIGNED','⬆ Enter GSTIN + certificate']].map(([n,s,desc]) => (
                  <div key={s} style={{ display: 'flex', gap: '.6rem', marginBottom: '.5rem' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: app.status === s ? 'var(--green)' : 'var(--creamd)', color: app.status === s ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{n}</div>
                    <div>
                      <div style={{ fontSize: '.75rem', fontWeight: 600, color: app.status === s ? 'var(--green)' : 'var(--text)' }}>{s.replace(/_/g,' ')}</div>
                      <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--creamd)', fontSize: '.73rem', color: 'var(--muted)' }}>
                Submitted: {new Date(app.submittedAt).toLocaleString('en-IN')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontWeight: 600, fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--navy)', marginBottom: '1rem' }}>{children}</div>;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ padding: '.4rem 0', borderBottom: '1px solid var(--creamd)', fontSize: '.83rem' }}>
      <div style={{ color: 'var(--muted)', fontSize: '.72rem', marginBottom: '.1rem' }}>{label}</div>
      <div style={{ fontWeight: 500, color: 'var(--navy)' }}>{value}</div>
    </div>
  );
}